import React from "react";
import cx from "classnames";

import { NotificationPreferences } from "src/types/experiences/storyboardSettings";

import Card from "../card";
import { useDraft } from "../useDraft";
import { canEdit } from "src/pages/experiences/storyboardSettings/utils/permissions";
import {
  isInteractiveContent,
  isSlideStoryboard,
} from "src/pages/experiences/storyboardSettings/utils/validation";
import styles from "./notificationsCard.module.scss";
import SegmentButton from "src/components/segmentButton";

type EmailKey = keyof NonNullable<NotificationPreferences["email"]>;

const ROWS: { key: EmailKey; label: string }[] = [
  { key: "leadCaptured", label: "Got a New Lead" },
  { key: "pageViewed", label: "Lead Viewed a Storyboard" },
  { key: "icDownload", label: "Lead Downloaded an Interactive Content" },
  { key: "assetViewed", label: "Lead Viewed an Asset" },
  { key: "assetDownload", label: "Lead Downloaded an Asset" },
];

const NotificationsCard: React.FC = () => {
  const { draft, setField } = useDraft();
  const email = draft?.notifications?.email ?? {};
  const editable = canEdit(draft?.role);
  const type = draft?.type;

  const visibleRows = ROWS.filter((row) => {
    if (row.key === "icDownload") return isInteractiveContent(type);
    if (row.key === "assetDownload") return !isSlideStoryboard(type);
    return true;
  });

  const toggle = (key: EmailKey) => {
    if (!editable) return;
    setField("notifications", {
      ...(draft?.notifications ?? {}),
      email: { ...email, [key]: !email[key] },
    });
  };

  return (
    <Card
      title="Notification Triggers"
      description="Customize alerts for specific events, ensuring real-time notifications for crucial activities and engagements"
    >
      <div className={cx(styles.list, !editable && styles.disabled)}>
        {visibleRows.map((row, idx) => (
          <React.Fragment key={row.key}>
            {idx > 0 && <div className={styles.divider} />}
            <div className={styles.row}>
              <span className={styles.label}>{row.label}</span>
              <div
                className={cx(styles.segmentWrapper, {
                  [styles.segmentSelected]: !!email[row.key],
                })}
              >
                <SegmentButton
                  listOfSegments={["Email"]}
                  selected={email[row.key] ? "Email" : undefined}
                  onChange={() => toggle(row.key)}
                />
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
};

export default NotificationsCard;
